(function () {
  'use strict';

  class CiariPdpArtists extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.viewport = this.querySelector('.cpda__viewport');
      this.slides = Array.from(this.querySelectorAll('.cpda__artist'));
      this.videos = Array.from(this.querySelectorAll('.cpda__video'));
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = window.matchMedia('(min-width: 1200px)');
      this.middle = Math.floor(this.slides.length / 2);
      this.index = this.desktop.matches ? 0 : this.middle;

      if (!this.viewport || this.slides.length < 2) this.dataset.cpdaStatic = '';
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onLocalKeyDown(event), { signal });
      document.addEventListener('keydown', (event) => this.onDocumentKeyDown(event), { signal });
      document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
      this.desktop.addEventListener('change', () => this.onModeChange(), { signal });

      const prev = this.querySelector('.cpda__nav--prev');
      const next = this.querySelector('.cpda__nav--next');
      if (prev) prev.addEventListener('click', () => this.step(-1), { signal });
      if (next) next.addEventListener('click', () => this.step(1), { signal });
      if (this.viewport) {
        this.viewport.addEventListener('scroll', () => this.queueSync(), { passive: true, signal });
        this.cleanupDrag = window.CiariCarousel.initializeHorizontalDragScroll(this.viewport);
      }

      if (this.videos.length && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.target.dataset.cpdaVisible = entry.isIntersecting ? 'true' : 'false';
            this.syncVideo(entry.target);
          });
        }, { threshold: 0.25 });
        this.videos.forEach((video) => this.observer.observe(video));
      }

      window.requestAnimationFrame(() => this.goTo(this.index, false));
    }

    step(direction) {
      if (this.desktop.matches || this.slides.length < 2) return;
      this.goTo((this.index + direction + this.slides.length) % this.slides.length, true);
    }

    goTo(index, animate) {
      const slide = this.slides[index];
      if (!slide || this.desktop.matches) {
        this.index = 0;
        this.syncSlideState();
        return;
      }
      this.index = index;
      const left = slide.offsetLeft - ((this.viewport.clientWidth - slide.offsetWidth) / 2);
      this.viewport.scrollTo({ left, behavior: animate && !this.reduceMotion.matches ? 'smooth' : 'auto' });
      this.syncSlideState();
      this.videos.forEach((video) => this.setMuted(video, true));
    }

    queueSync() {
      if (this.desktop.matches || this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        const center = this.viewport.scrollLeft + (this.viewport.clientWidth / 2);
        let nearest = 0;
        let distance = Infinity;
        this.slides.forEach((slide, index) => {
          const nextDistance = Math.abs((slide.offsetLeft + slide.offsetWidth / 2) - center);
          if (nextDistance < distance) {
            distance = nextDistance;
            nearest = index;
          }
        });
        if (nearest !== this.index) {
          this.index = nearest;
          this.videos.forEach((video) => this.setMuted(video, true));
        }
        this.syncSlideState();
      });
    }

    syncSlideState() {
      this.slides.forEach((slide, index) => {
        const active = this.desktop.matches || index === this.index;
        slide.toggleAttribute('data-active', active);
        const meta = slide.querySelector('.cpda__meta');
        if (meta && active) meta.removeAttribute('aria-hidden');
        else if (meta) meta.setAttribute('aria-hidden', 'true');
      });
    }

    onModeChange() {
      window.requestAnimationFrame(() => {
        this.index = this.desktop.matches ? 0 : this.middle;
        if (!this.desktop.matches) this.goTo(this.index, false);
        else {
          this.viewport.scrollTo({ left: 0, behavior: 'auto' });
          this.syncSlideState();
        }
      });
    }

    play(video) {
      this.hydrate(video);
      const promise = video.play();
      if (promise && promise.catch) promise.catch(function () {});
    }

    hydrate(video) {
      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      if (!sources.length) return;
      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
    }

    syncVideo(video) {
      const visible = video.dataset.cpdaVisible === 'true' && !document.hidden;
      const button = video.closest('[data-cpda-video-toggle]');
      const userEnabled = button && button.getAttribute('aria-pressed') === 'true';
      if (visible && (!this.reduceMotion.matches || userEnabled)) this.play(video);
      else video.pause();
      if (!visible && userEnabled) this.setMuted(video, true);
    }

    setMuted(video, muted) {
      const button = video.closest('[data-cpda-video-toggle]');
      if (!button) return;
      video.muted = muted;
      button.setAttribute('aria-pressed', muted ? 'false' : 'true');
      button.setAttribute('aria-label', muted ? button.dataset.playLabel : button.dataset.muteLabel);
    }

    onClick(event) {
      const button = event.target.closest('[data-cpda-video-toggle]');
      if (!button || !this.contains(button)) return;
      const video = button.querySelector('.cpda__video');
      if (!video) return;
      const slide = button.closest('.cpda__artist');
      const index = slide ? Number(slide.dataset.cpdaIndex) : NaN;
      if (!this.desktop.matches && Number.isInteger(index) && index !== this.index) this.goTo(index, true);
      const willUnmute = video.muted;
      this.videos.forEach((candidate) => { if (candidate !== video) this.setMuted(candidate, true); });
      this.setMuted(video, !willUnmute);
      this.play(video);
    }

    onLocalKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!event.target.closest('.cpda__viewport, .cpda__nav')) return;
      event.preventDefault();
      this.step(event.key === 'ArrowRight' ? 1 : -1);
    }

    onDocumentKeyDown(event) {
      if (event.key === 'Escape') this.videos.forEach((video) => this.setMuted(video, true));
    }

    onVisibilityChange() {
      this.videos.forEach((video) => this.syncVideo(video));
    }

    disconnectedCallback() {
      this.initialized = false;
      if (this.controller) this.controller.abort();
      if (this.cleanupDrag) this.cleanupDrag();
      if (this.observer) this.observer.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.videos.forEach((video) => video.pause());
    }
  }

  if (!customElements.get('ciari-pdp-artists')) customElements.define('ciari-pdp-artists', CiariPdpArtists);
})();
